"use client";

import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Context } from "../hooks/useFilterContext";
import { usePathname, useRouter } from "../../../navigation";

export interface FilterContextProps {
  children?: React.ReactNode;
}

const convertSearchParamsToObject = (searchParams: URLSearchParams) => {
  const formState = {};
  searchParams.forEach((value, key) => {
    formState[key] = value.split("~");
  });
  return formState;
};

const applyStateToSearchParams = (
  searchParams: URLSearchParams,
  formState: Record<string, any[]>,
) => {
  const params = new URLSearchParams(searchParams); // Do not reset the other params!
  Object.entries(formState).forEach(([key, values]) => {
    if (key === "__clearKeys__") {
      for (const clearKey of values as Array<string>) {
        params.delete(clearKey);
      }
      return;
    }
    if (values?.[0]) {
      params.set(key, (values as Array<any>).join("~"));
    } else {
      params.delete(key);
    }
  });
  return params;
};
const FilterContext: React.FC<FilterContextProps> = ({ children }) => {
  const searchParams = useSearchParams();
  const [formState, setFormState] = useState<Record<string, any[]>>(
    convertSearchParamsToObject(searchParams),
  );

  const setFilterValues = useCallback(
    (paramName, values: string[]) => {
      const newFormState = {
        ...formState,
        [paramName]: values,
        __clearKeys__: [],
      };
      setFormState(newFormState);
    },
    [setFormState, formState],
  );

  const clearFilterValues = useCallback(() => {
    /* For some reason it was set to this before: 
    setFormState(formState?.sort ? { sort: formState?.sort } : {});
    we try to get around several problems by storing the keys to delete
    */
    const clearKeys = Object.keys(formState).filter((key) => {
      return key !== "sort";
    });
    setFormState(
      formState?.sort
        ? { sort: formState?.sort, __clearKeys__: clearKeys }
        : { __clearKeys__: clearKeys },
    );
  }, [setFormState, formState]);

  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const params = applyStateToSearchParams(
      new URLSearchParams(searchParams),
      formState,
    );
    router.replace(pathname + "?" + params.toString(), {
      scroll: false,
    });
  }, [router, pathname, formState, searchParams]);

  const isAnyFilterSet =
    Object.entries(formState).filter(
      ([k, f]) => (f as any)?.length && k !== "sort" && k !== "__clearKeys__",
    ).length > 0;

  return (
    <Context.Provider
      value={{
        formState,
        setFilterValues,
        clearFilterValues,
        isAnyFilterSet,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default FilterContext;
